"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { Wrench, X } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { formatDateTime, formatTime } from "@/lib/format-date";
import { isCompanionView } from "@/lib/instance-path";
import { useHydrated } from "@/lib/use-hydrated";

/**
 * Site-wide notice for a platform maintenance window (lib/maintenance.ts).
 * Rendered by the root layout whenever a window is scheduled or running:
 * before starts_at it announces the window; while active (only reachable on
 * ungated pages — agent surfaces render <MaintenanceHold/> instead) it says
 * agents are down and until when.
 *
 * Times are formatted after hydration only (the boxes run TZ=UTC; the
 * visitor's zone is unknown to the server render), in the site language with
 * the app-wide 24-hour convention (lib/format-date.ts). Same bottom-left card
 * placement as <UpdateNotice/> (clears the fixed top nav on marketing pages).
 *
 * Dismissal persists in sessionStorage — someone who dismissed it doesn't
 * want it back on every reload (unlike <UpdateNotice/>, where we really do
 * want the reload). Keyed by phase + starts_at, so dismissing the advance
 * notice never suppresses the in-progress notice, and neither suppresses a
 * future window. Storage access is try/caught: a blocked or absent
 * sessionStorage must degrade to page-load dismissal, never break the page.
 *
 * Not in the companion view (the chat the browser side panel frames): there
 * the card would sit over the message box, and the panel shows the window in
 * its own header instead (apps/companion-extension/src/panel.ts).
 */
const DISMISS_KEY = "sq_maintenance_dismissed";

const readDismissed = (value: string): boolean => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === value;
  } catch {
    return false;
  }
};

const writeDismissed = (value: string): void => {
  try {
    sessionStorage.setItem(DISMISS_KEY, value);
  } catch {
    // Per-tab convenience only — fine to lose.
  }
};

export function MaintenanceBanner({
  phase,
  startsAt,
  endsAt,
}: {
  phase: "upcoming" | "active";
  startsAt: string;
  endsAt: string;
}) {
  const { t, i18n } = useLingui();
  const [dismissed, setDismissed] = useState(false);
  const hydrated = useHydrated();
  const pathname = usePathname();

  const dismissValue = `${phase}:${startsAt}`;
  if (!hydrated || dismissed || isCompanionView(pathname) || readDismissed(dismissValue)) return null;

  const range = phase === "upcoming"
    ? `${formatDateTime(startsAt, { timeZone: null, locale: i18n.locale })} – ${formatTime(endsAt, { timeZone: null, locale: i18n.locale })}`
    : formatTime(endsAt, { timeZone: null, locale: i18n.locale });

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-sm items-start gap-3 rounded-xl border border-ink-mid bg-ink-soft p-4 text-cream shadow-2xl sm:left-6 sm:right-auto sm:mx-0"
    >
      <Wrench className="mt-0.5 size-4 shrink-0 text-signal" />
      <p className="flex-1 text-sm leading-snug text-stone-soft">
        {phase === "upcoming" ? (
          <Trans>Scheduled maintenance: {range}. Agents will be briefly unavailable during this window.</Trans>
        ) : (
          <Trans>Maintenance in progress — agents are back by about {range}.</Trans>
        )}
      </p>
      <button
        onClick={() => { writeDismissed(dismissValue); setDismissed(true); }}
        aria-label={t`Dismiss`}
        className="shrink-0 text-stone-soft transition hover:text-cream"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
